import { faSquarePollVertical } from "@fortawesome/pro-solid-svg-icons"
import { Box, Typography } from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import ActionButtonIcon from "components/Actions/ActionIconButton/ActionIconButton"
import { ActionIconButtonTypeEnum } from "components/Actions/ActionIconButton/actionIcon.interface"
import ClpTable from "components/ClpTable"
import { DatePickerColumnFilter, MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import ChipInfoIcon from "components/Status/ChipInfo/ChipInfoIcon"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { learningStatusEnum } from "pages/learning/pages/admin/interface/learning.interface"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router"
import { CertificateDocument } from "shared/learning/components/CertificateDocument"
import { StyledTableContainer } from "shared/styled/Global"
import { validTo } from "./helpers/AttendeeStatus"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"
import { recertificationPeriodStatus, RecertStatusChip } from "./helpers/recertificationHelpers"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"

export const AttendeeTable = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { items, setShowReminderModal, setClickedRowIds, examinationName } = props

  const columns = [
    {
      accessorKey: "userFullName",
      header: t("global.table.name", "Name")
    },
    {
      accessorKey: "email",
      header: t("global.table.email", "Email")
    },

    {
      accessorFn: (row) => row.status,
      id: "status",
      cell: ({ cell }) => {
        const label =
          cell.row.original.lastDateForPassedExam &&
          moment(cell.row.original.lastDateForPassedExam).diff(moment(), "days") > 0
            ? `${t("learning.titles.expires_in", "Expires in 30 days")} ${moment(
                cell.row.original.lastDateForPassedExam
              ).diff(moment(), "days")} ${t("global.days", "days")}`
            : moment(cell.row.original.lastDateForPassedExam).diff(moment(), "days") < 0
            ? `${t("learning.titles.expired_for", "Expired for")} ${moment(
                cell.row.original.lastDateForPassedExam
              )
                .diff(moment(), "days")
                .toString()
                .replace("-", "")} ${t("global.days_ago", "days ago")}`
            : `${t("learning.title_expired", "Expired")}`
        return <ChipInfoIcon type={learningStatusEnum(cell.row.original.status)} label={label} />
      },
      header: t("global.table.status", "Status"),
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        filterHeader: t("global.table.status", "Status"),
        mainFilter: true,
        filter: MultiSelectFilter,
        filterOptions: [
          {
            name: t("learning.titles.not_started", "Not started"),
            value: 0
          },
          {
            name: t("learning.titles.passed", "Passed"),
            value: 1
          },
          {
            name: t("learning.titles.failed", "Failed"),
            value: 2
          },
          {
            name: t("learning.titles.expires_in", "Expires in"),
            value: 3
          }
        ]
      }
    },
    {
      accessorKey: "recertificationPeriodStatus",
      header: t("learning.titles.recertification_period", "Recertification period"),
      accessorFn: (row) => row.recertificationPeriodStatus,
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        filterHeader: t("learning.titles.recert_period", "Recert period"),
        mainFilter: true,
        filter: MultiSelectFilter,
        filterOptions: [
          {
            name: t("learning.titles.still_in_recert", "In progress"),
            value: 0
          },
          {
            name: t("learning.titles.done_in_recert", "Done"),
            value: 1
          },
          {
            name: t("global.starting_soon", "Starting soon"),
            value: 2
          }
        ]
      },
      cell: ({ cell }) => {
        const label = recertificationPeriodStatus(cell.row.original.recertificationPeriodStatus, t)
        return (
          <RecertStatusChip
            type={cell.row.original.recertificationPeriodStatus}
            label={label}
            date={cell.row.original.recertificationPeriodDate}
          />
        )
      }
    },
    {
      accessorFn: (row) => {
        return row.latestActivity ? moment(row.latestActivity).format("YYYY-MM-DD") : ""
      },
      header: t("global.table.last_approved", "Last approved"),
      meta: {
        filter: DatePickerColumnFilter
      }
    },
    {
      accessorKey: "lastDateForPassedExam",
      header: t("global.table.valid_to", "Valid to"),
      cell: ({ cell }) => {
        var validObj = validTo(cell.row.original.lastDateForPassedExam, t)
        return (
          <Box color={`${validObj?.color}.main`}>
            <FontAwesomeIcon style={{ color: `${validObj?.color}.main` }} icon={validObj?.icon} />
            <Typography ml={1} color={`${validObj?.color}.main`} variant="labelMedium">
              {validObj?.label}
            </Typography>
          </Box>
        )
      }
    },
    {
      accessorFn: (row) => {
        return row.lastReminderSent ? moment.utc(row.lastReminderSent).local().format("YYYY-MM-DD HH:mm") : ""
      },
      header: t("learning.titles.send_reminder", "Send reminder")
    },
    {
      accessorKey: "manager",
      header: t("global.manager", "Manager")
    },
    {
      cell: ({ cell }) => {
        return (
          ((cell.row.original.email && cell.row.original.status !== 1) ||
            (cell.row.original.email &&
              cell.row.original.lastDateForPassedExam !== null &&
              moment(cell.row.original.lastDateForPassedExam).diff(moment(), "days") < 30)) && (
            <ActionButtonIcon
              type={ActionIconButtonTypeEnum.SendNotification}
              handleOnClick={() => {
                setClickedRowIds({
                  userExaminationId: cell.row.original.id,
                  userId: cell.row.original.userId,
                  userName: cell.row.original.userFullName
                })
                setShowReminderModal(true)
              }}
            />
          )
        )
      },
      accessorKey: "action",
      header: "",
      meta: {
        hiddenFilter: true
      }
    },
    {
      accessorKey: "edit",
      header: "",

      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            {cell.row.original.status === 1 && (
              <TableButton
                type={TableButtonTypeEnum.Download}
                handleOnClick={async () => {
                  let blobPDF = await pdf(
                    <CertificateDocument
                      userFullName={cell.row.original.userFullName}
                      examinationName={examinationName}
                      completedDate={cell.row.original.latestActivity}
                    />
                  ).toBlob()

                  const link = document.createElement("a")
                  link.href = window.URL.createObjectURL(blobPDF)
                  link.download = `Utbildningsbevis-${cell.row.original.userFullName}.pdf`
                  link.click()
                  window.URL.revokeObjectURL(link.href)
                }}
              />
            )}
            {cell.row.original.hasResult && (
              <TableButton
                type={TableButtonTypeEnum.Custom}
                custom={{ icon: faSquarePollVertical, label: "Resultat" }}
                handleOnClick={() => {
                  history.push(allRoutes.LearningAdminResultsDetails(cell.row.original.resultId))
                }}
              />
            )}
          </StyledTableButtonGroup>
        )
      },
      meta: {
        align: "right"
      }
    }
  ]

  const initialSort = [
    { id: "status", desc: true },
    { id: "userFullName", desc: false }
  ]

  return (
    <StyledTableContainer>
      <ClpTable
        columns={columns}
        rows={items.status === 2 && items.data}
        loadingState={items.status}
        initialSortParams={initialSort}
        tableId={ClpTableEnum.LearningAdminResults}
      />
    </StyledTableContainer>
  )
}
